import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuthedGet } from "@/pages/Admin";
import { fmtDate, fmtTime } from "@/lib/api";
import { Attachment } from "@/components/chat/Messages";
import { BanDialog } from "@/components/admin/BanDialog";

export const TranscriptDialog = ({ id, onClose }) => {
  const req = useAuthedGet();
  const [data, setData] = useState(null);
  const [banTarget, setBanTarget] = useState(null);

  useEffect(() => {
    if (!id) return setData(null);
    const load = () => req(`/admin/conversations/${id}`).then((r) => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [id]); // eslint-disable-line

  const terminate = async () => {
    try {
      await req(`/admin/conversations/${id}/terminate`, { method: "post" });
      toast.success("Beszélgetés lezárva");
      onClose();
    } catch { toast.error("Nem sikerült lezárni"); }
  };

  const conv = data?.conversation;
  const first = conv?.participants?.[0]?.peer_id;

  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-hidden border-white/10 bg-[#1A152E] p-0" data-testid="admin-transcript-modal">
        <DialogHeader className="border-b border-white/10 p-6">
          <DialogTitle className="font-display text-2xl">{conv ? conv.participants.map((p) => p.nick).join(" ↔ ") : "Betöltés…"}</DialogTitle>
          <DialogDescription>
            {conv && `${fmtDate(conv.started_at)} – ${conv.ended_at ? fmtDate(conv.ended_at) : "folyamatban"} · ${conv.message_count} üzenet`}
          </DialogDescription>
          {conv?.participants?.some((p) => p.ip || p.device_id) && (
            <div className="mt-2 space-y-2 text-xs text-muted-foreground" data-testid="admin-participant-info">
              {conv.participants.map((p) => (
                <div key={p.peer_id} className="flex items-start gap-2">
                  <p className="min-w-0 flex-1 break-all"><b className="text-white">{p.nick}</b> · IP: {p.ip || "—"} · belépett: {fmtDate(p.joined_at)} · {p.user_agent || "—"}</p>
                  <button onClick={() => setBanTarget({ ...p, conversation_id: conv.id })} data-testid={`transcript-ban-${p.peer_id}`}
                    className="shrink-0 rounded-full bg-[#EF4444]/15 px-3 py-1 text-[11px] font-bold text-[#EF4444] transition-colors hover:bg-[#EF4444]/25">Kitiltás</button>
                </div>
              ))}
            </div>
          )}
          {conv?.status === "live" && (
            <button onClick={terminate} data-testid="admin-terminate-button"
              className="mt-3 w-fit rounded-full bg-[#EF4444] px-4 py-1.5 text-xs font-bold text-white transition-transform hover:scale-105">Beszélgetés lezárása</button>
          )}
        </DialogHeader>
        <div className="scroll-thin max-h-[60vh] space-y-3 overflow-y-auto p-6" data-testid="admin-transcript-messages">
          {data?.messages?.length === 0 && <p className="text-center text-sm text-muted-foreground">Nem írtak üzenetet.</p>}
          {data?.messages?.map((m) => {
            const left = m.sender_peer_id === first;
            return (
              <div key={m.id} className={`flex flex-col ${left ? "items-start" : "items-end"}`} data-testid="admin-transcript-message">
                <span className="mb-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{m.sender_nick} · {fmtTime(m.created_at)}</span>
                <div className={`max-w-[85%] break-words rounded-2xl px-4 py-2 text-sm ${left ? "bg-[#241E3D]" : "bg-[#7C3AED]"}`}>
                  {m.text && <p className="whitespace-pre-wrap">{m.text}</p>}
                  {m.attachment && <Attachment a={m.attachment} />}
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
      <BanDialog target={banTarget} onClose={() => setBanTarget(null)} />
    </Dialog>
  );
};
